import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  cardDetailSchema,
  createCustomFieldDefinitionBodySchema,
  createCustomFieldOptionBodySchema,
  customFieldDefinitionSchema,
  customFieldHiddenPatchBodySchema,
  customFieldNamePatchBodySchema,
  customFieldOptionLabelPatchBodySchema,
  customFieldOptionPositionPatchBodySchema,
  customFieldPositionPatchBodySchema,
  customFieldShowOnFrontPatchBodySchema,
  customFieldValuePatchBodySchema,
  type BoardContents,
  type CardCustomField,
  type CardCustomFieldBadge,
  type CardDetail,
  type CreateCustomFieldDefinitionBody,
  type CustomFieldDefinition,
  type CustomFieldValue,
  type CustomFieldValuePatchBody,
} from '@molt/shared';
import { z } from 'zod';
import { announce } from './announce';
import { del, emptyResponseSchema, get, patch, post } from './api';
import { applyCardDetailToBoardContents } from './cards';
import { setBoardContentsQueries } from './cross-board-move';
import { useInterfaceLanguage } from './interface-language';
import { queryKeys } from './query-keys';

const customFieldDefinitionListSchema = z.array(customFieldDefinitionSchema);

type CreateCustomFieldOptionBody = z.infer<typeof createCustomFieldOptionBodySchema>;
type CustomFieldHiddenPatchBody = z.infer<typeof customFieldHiddenPatchBodySchema>;
type CustomFieldNamePatchBody = z.infer<typeof customFieldNamePatchBodySchema>;
type CustomFieldOptionLabelPatchBody = z.infer<
  typeof customFieldOptionLabelPatchBodySchema
>;
type CustomFieldOptionPositionPatchBody = z.infer<
  typeof customFieldOptionPositionPatchBodySchema
>;
type CustomFieldPositionPatchBody = z.infer<typeof customFieldPositionPatchBodySchema>;
type CustomFieldShowOnFrontPatchBody = z.infer<
  typeof customFieldShowOnFrontPatchBodySchema
>;

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export function getBoardCustomFields(boardId: string): Promise<CustomFieldDefinition[]> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/custom-fields`,
    customFieldDefinitionListSchema,
  );
}

export function createCustomFieldDefinition(
  boardId: string,
  body: CreateCustomFieldDefinitionBody,
): Promise<CustomFieldDefinition> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/custom-fields`,
    customFieldDefinitionSchema,
    createCustomFieldDefinitionBodySchema.parse(body),
  );
}

export function renameCustomFieldDefinition(
  fieldId: string,
  body: CustomFieldNamePatchBody,
): Promise<CustomFieldDefinition> {
  return patch(
    `/custom-fields/${encodeURIComponent(fieldId)}/name`,
    customFieldDefinitionSchema,
    customFieldNamePatchBodySchema.parse(body),
  );
}

export function setCustomFieldHidden(
  fieldId: string,
  body: CustomFieldHiddenPatchBody,
): Promise<CustomFieldDefinition> {
  return patch(
    `/custom-fields/${encodeURIComponent(fieldId)}/hidden`,
    customFieldDefinitionSchema,
    customFieldHiddenPatchBodySchema.parse(body),
  );
}

export function setCustomFieldShowOnFront(
  fieldId: string,
  body: CustomFieldShowOnFrontPatchBody,
): Promise<CustomFieldDefinition> {
  return patch(
    `/custom-fields/${encodeURIComponent(fieldId)}/show-on-front`,
    customFieldDefinitionSchema,
    customFieldShowOnFrontPatchBodySchema.parse(body),
  );
}

export function moveCustomFieldDefinition(
  fieldId: string,
  body: CustomFieldPositionPatchBody,
): Promise<CustomFieldDefinition> {
  return patch(
    `/custom-fields/${encodeURIComponent(fieldId)}/position`,
    customFieldDefinitionSchema,
    customFieldPositionPatchBodySchema.parse(body),
  );
}

export function deleteCustomFieldDefinition(fieldId: string): Promise<null> {
  return del(`/custom-fields/${encodeURIComponent(fieldId)}`, emptyResponseSchema);
}

export function createCustomFieldOption(
  fieldId: string,
  body: CreateCustomFieldOptionBody,
): Promise<CustomFieldDefinition> {
  return post(
    `/custom-fields/${encodeURIComponent(fieldId)}/options`,
    customFieldDefinitionSchema,
    createCustomFieldOptionBodySchema.parse(body),
  );
}

export function renameCustomFieldOption(
  optionId: string,
  body: CustomFieldOptionLabelPatchBody,
): Promise<CustomFieldDefinition> {
  return patch(
    `/custom-field-options/${encodeURIComponent(optionId)}/label`,
    customFieldDefinitionSchema,
    customFieldOptionLabelPatchBodySchema.parse(body),
  );
}

export function moveCustomFieldOption(
  optionId: string,
  body: CustomFieldOptionPositionPatchBody,
): Promise<CustomFieldDefinition> {
  return patch(
    `/custom-field-options/${encodeURIComponent(optionId)}/position`,
    customFieldDefinitionSchema,
    customFieldOptionPositionPatchBodySchema.parse(body),
  );
}

export function deleteCustomFieldOption(
  optionId: string,
): Promise<CustomFieldDefinition> {
  return del(
    `/custom-field-options/${encodeURIComponent(optionId)}`,
    customFieldDefinitionSchema,
  );
}

export function patchCardCustomFieldValue(
  cardId: string,
  fieldId: string,
  body: CustomFieldValuePatchBody,
): Promise<CardDetail> {
  return patch(
    `/cards/${encodeURIComponent(cardId)}/custom-fields/${encodeURIComponent(fieldId)}/value`,
    cardDetailSchema,
    customFieldValuePatchBodySchema.parse(body),
  );
}

export function deleteCardCustomFieldValue(
  cardId: string,
  fieldId: string,
): Promise<CardDetail> {
  return del(
    `/cards/${encodeURIComponent(cardId)}/custom-fields/${encodeURIComponent(fieldId)}/value`,
    cardDetailSchema,
  );
}

export function boardCustomFieldsQueryOptions(boardId: string) {
  return queryOptions({
    queryKey: queryKeys.boards.customFields(boardId),
    queryFn: () => getBoardCustomFields(boardId),
  });
}

// ---------------------------------------------------------------------------
// Variables
// ---------------------------------------------------------------------------

export interface CreateCustomFieldDefinitionVariables {
  readonly boardId: string;
  readonly body: CreateCustomFieldDefinitionBody;
}

export interface RenameCustomFieldDefinitionVariables {
  readonly boardId: string;
  readonly fieldId: string;
  readonly name: CustomFieldNamePatchBody['name'];
}

export interface SetCustomFieldHiddenVariables {
  readonly boardId: string;
  readonly fieldId: string;
  readonly hidden: CustomFieldHiddenPatchBody['hidden'];
}

export interface SetCustomFieldShowOnFrontVariables {
  readonly boardId: string;
  readonly fieldId: string;
  readonly showOnFront: CustomFieldShowOnFrontPatchBody['showOnFront'];
}

export interface MoveCustomFieldDefinitionVariables {
  readonly boardId: string;
  readonly fieldId: string;
  readonly previousDefinitionId: string | null;
  readonly nextDefinitionId: string | null;
}

export interface DeleteCustomFieldDefinitionVariables {
  readonly boardId: string;
  readonly fieldId: string;
}

export interface CreateCustomFieldOptionVariables {
  readonly boardId: string;
  readonly fieldId: string;
  readonly label: CreateCustomFieldOptionBody['label'];
}

export interface RenameCustomFieldOptionVariables {
  readonly boardId: string;
  readonly fieldId: string;
  readonly optionId: string;
  readonly label: CustomFieldOptionLabelPatchBody['label'];
}

export interface MoveCustomFieldOptionVariables {
  readonly boardId: string;
  readonly fieldId: string;
  readonly optionId: string;
  readonly previousOptionId: string | null;
  readonly nextOptionId: string | null;
}

export interface DeleteCustomFieldOptionVariables {
  readonly boardId: string;
  readonly fieldId: string;
  readonly optionId: string;
}

interface UpdateCardCustomFieldValueVariablesBase {
  readonly boardId: string;
  readonly cardId: string;
  readonly fieldId: string;
}

export type UpdateCardCustomFieldValueVariables =
  | (UpdateCardCustomFieldValueVariablesBase & {
      readonly body: CustomFieldValuePatchBody;
      readonly value?: never;
    })
  | (UpdateCardCustomFieldValueVariablesBase & {
      readonly value: CustomFieldValuePatchBody;
      readonly body?: never;
    });

export interface ClearCardCustomFieldValueVariables {
  readonly boardId: string;
  readonly cardId: string;
  readonly fieldId: string;
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function preserveServerOrder<T>(definitions: readonly T[]): T[] {
  return [...definitions];
}

export function customFieldValueLabel(value: CustomFieldValue | null): string {
  if (value === null) return '';

  switch (value.type) {
    case 'text':
      return value.value;
    case 'number':
      return String(value.value);
    case 'checkbox':
      return 'Checked';
    case 'date':
      return value.value.slice(0, 10);
    case 'dropdown':
      return value.label;
  }
}

export function customFieldBadgeLabel(badge: CardCustomFieldBadge): string {
  const value = customFieldValueLabel(badge.value);
  return badge.value.type === 'checkbox' ? badge.name : `${badge.name}: ${value}`;
}

export function replaceCustomFieldDefinition(
  definitions: readonly CustomFieldDefinition[] | undefined,
  definition: CustomFieldDefinition,
): CustomFieldDefinition[] | undefined {
  if (definitions === undefined) return undefined;
  const found = definitions.some((candidate) => candidate.id === definition.id);
  return preserveServerOrder(
    found
      ? definitions.map((candidate) =>
          candidate.id === definition.id ? definition : candidate,
        )
      : [...definitions, definition],
  );
}

export function removeCustomFieldDefinition(
  definitions: readonly CustomFieldDefinition[] | undefined,
  fieldId: string,
): CustomFieldDefinition[] | undefined {
  if (definitions === undefined) return undefined;
  return definitions.filter((definition) => definition.id !== fieldId);
}

function fieldFromDefinition(
  definition: CustomFieldDefinition,
  value: CustomFieldValue | null,
): CardCustomField {
  return { ...definition, value };
}

export function replaceCardCustomFieldDefinition(
  detail: CardDetail | undefined,
  definition: CustomFieldDefinition,
): CardDetail | undefined {
  if (detail?.boardId !== definition.boardId) return detail;

  if (definition.hidden) {
    return removeCardCustomFieldDefinition(detail, definition.id);
  }

  const existing = detail.customFields.find((field) => field.id === definition.id);
  const fields = detail.customFields.some((field) => field.id === definition.id)
    ? detail.customFields.map((field) =>
        field.id === definition.id ? fieldFromDefinition(definition, field.value) : field,
      )
    : [...detail.customFields, fieldFromDefinition(definition, existing?.value ?? null)];

  return { ...detail, customFields: preserveServerOrder(fields) };
}

export function removeCardCustomFieldDefinition(
  detail: CardDetail | undefined,
  fieldId: string,
): CardDetail | undefined {
  if (detail === undefined) return undefined;
  return {
    ...detail,
    customFields: detail.customFields.filter((field) => field.id !== fieldId),
  };
}

function isCardDetailCacheEntry(value: unknown): value is CardDetail {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { readonly boardId?: unknown }).boardId === 'string' &&
    Array.isArray((value as { readonly customFields?: unknown }).customFields)
  );
}

export function replaceCachedCardCustomFieldDefinition(
  previous: unknown,
  definition: CustomFieldDefinition,
): unknown {
  return isCardDetailCacheEntry(previous)
    ? replaceCardCustomFieldDefinition(previous, definition)
    : previous;
}

export function removeCachedCardCustomFieldDefinition(
  previous: unknown,
  fieldId: string,
): unknown {
  return isCardDetailCacheEntry(previous)
    ? removeCardCustomFieldDefinition(previous, fieldId)
    : previous;
}

export function customFieldBadgesFromCardDetail(
  detail: CardDetail,
): CardCustomFieldBadge[] {
  return detail.customFields.flatMap((field) =>
    !field.hidden && field.showOnFront && field.value !== null
      ? [
          {
            definitionId: field.id,
            name: field.name,
            type: field.type,
            value: field.value,
          },
        ]
      : [],
  );
}

export function applyCustomFieldCardDetailToBoardContents(
  previous: BoardContents | undefined,
  detail: CardDetail,
): BoardContents | undefined {
  const next = applyCardDetailToBoardContents(previous, detail);
  if (next === undefined) return undefined;

  const customFieldBadges = customFieldBadgesFromCardDetail(detail);
  return {
    ...next,
    lists: next.lists.map((list) => ({
      ...list,
      cards: list.cards.map((card) =>
        card.id === detail.id ? { ...card, customFieldBadges } : card,
      ),
    })),
  };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function updateDefinitionCaches(
  queryClient: QueryClient,
  boardId: string,
  definition: CustomFieldDefinition,
): void {
  queryClient.setQueryData(queryKeys.boards.customFields(boardId), (previous) =>
    replaceCustomFieldDefinition(
      previous as readonly CustomFieldDefinition[] | undefined,
      definition,
    ),
  );
  queryClient.setQueriesData<unknown>(
    { queryKey: queryKeys.cards.all() },
    (previous: unknown) => replaceCachedCardCustomFieldDefinition(previous, definition),
  );
}

function invalidateCustomFieldPresentation(
  queryClient: QueryClient,
  boardId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.boards.contents(boardId),
  });
  void queryClient.invalidateQueries({ queryKey: queryKeys.cards.all() });
}

function handleDefinitionSuccess(
  queryClient: QueryClient,
  boardId: string,
  definition: CustomFieldDefinition,
): void {
  updateDefinitionCaches(queryClient, boardId, definition);
  void queryClient.invalidateQueries({
    queryKey: queryKeys.boards.customFields(boardId),
  });
  invalidateCustomFieldPresentation(queryClient, boardId);
}

/**
 * A card's custom-field value changed: write the server's card detail into the
 * card cache, and patch the recomputed front badges into every cached
 * board-contents variant.
 *
 * ---------------------------------------------------------------------------
 * ⚠ AND DELIBERATELY DOES NOT INVALIDATE — EVEN THOUGH THE KEYWORD FACET NOW
 * MATCHES CUSTOM FIELD VALUES
 * ---------------------------------------------------------------------------
 * `FR-SEARCH-007`'s keyword facet has been widened to reach custom field values
 * (`packages/shared/src/board-filter.ts`'s keyword contract and
 * `apps/server/src/boards/filter.ts`'s `matchesKeywordFacet`): a term now
 * matches the card **title OR any one of the card's text / dropdown-label
 * custom-field values**. So a value edit can change whether the card belongs in
 * a keyword-filtered `GET /boards/:id/contents` or `GET /boards/:id/table`
 * result at all — a **membership** question, which only the server can answer
 * and which the badge patch below cannot even approximate.
 *
 * That reconciliation is **already owned, and by exactly one producer.** The
 * value routes emit `custom.field_value_changed` with `boardId` on a column
 * (`apps/server/src/routes/custom-fields.ts`), `publishEvents` fans it out on
 * the board channel, and `socket.ts` routes every non-typing frame through
 * `eventBlastRadius`, whose `queryKeys.boards.detail(boardId)` **prefix** reaches
 * `['boards', id, 'contents', filterKey]` and `['boards', id, 'table', …]`
 * alike. The board socket applies no actor filter, so the member who made the
 * edit receives their own frame like everybody else.
 *
 * Invalidating here as well would refetch the whole board twice per edit and
 * make this file a **second producer of one reconciliation** — the same defect
 * shape the widened facet already has to live with on the server
 * (`calendar-view.ts`'s rival keyword predicate). The card *title* path settles
 * the house answer: title has been keyword-matched since the facet existed, and
 * `useRenameCard` patches without invalidating for this reason.
 */
function handleValueSuccess(
  queryClient: QueryClient,
  boardId: string,
  detail: CardDetail,
): void {
  queryClient.setQueryData(queryKeys.cards.detail(detail.id), detail);
  setBoardContentsQueries(queryClient, boardId, (previous) =>
    applyCustomFieldCardDetailToBoardContents(previous, detail),
  );
}

export function useCreateCustomFieldDefinition(): UseMutationResult<
  CustomFieldDefinition,
  unknown,
  CreateCustomFieldDefinitionVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) =>
      createCustomFieldDefinition(variables.boardId, variables.body),
    onSuccess: (definition, variables) =>
      handleDefinitionSuccess(queryClient, variables.boardId, definition),
    onError: () => announce(t('primitives.customFieldHooks.createDefinitionFailed')),
  });
}

export function useRenameCustomFieldDefinition(): UseMutationResult<
  CustomFieldDefinition,
  unknown,
  RenameCustomFieldDefinitionVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) =>
      renameCustomFieldDefinition(variables.fieldId, { name: variables.name }),
    onSuccess: (definition, variables) =>
      handleDefinitionSuccess(queryClient, variables.boardId, definition),
    onError: () => announce(t('primitives.customFieldHooks.renameDefinitionFailed')),
  });
}

export function useSetCustomFieldHidden(): UseMutationResult<
  CustomFieldDefinition,
  unknown,
  SetCustomFieldHiddenVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) =>
      setCustomFieldHidden(variables.fieldId, { hidden: variables.hidden }),
    onSuccess: (definition, variables) =>
      handleDefinitionSuccess(queryClient, variables.boardId, definition),
    onError: () => announce(t('primitives.customFieldHooks.updateVisibilityFailed')),
  });
}

export function useSetCustomFieldShowOnFront(): UseMutationResult<
  CustomFieldDefinition,
  unknown,
  SetCustomFieldShowOnFrontVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) =>
      setCustomFieldShowOnFront(variables.fieldId, {
        showOnFront: variables.showOnFront,
      }),
    onSuccess: (definition, variables) =>
      handleDefinitionSuccess(queryClient, variables.boardId, definition),
    onError: () => announce(t('primitives.customFieldHooks.updateFrontBadgeFailed')),
  });
}

export function useMoveCustomFieldDefinition(): UseMutationResult<
  CustomFieldDefinition,
  unknown,
  MoveCustomFieldDefinitionVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) =>
      moveCustomFieldDefinition(variables.fieldId, {
        previousDefinitionId: variables.previousDefinitionId,
        nextDefinitionId: variables.nextDefinitionId,
      }),
    onSuccess: (definition, variables) =>
      handleDefinitionSuccess(queryClient, variables.boardId, definition),
    onError: () => announce(t('primitives.customFieldHooks.reorderDefinitionFailed')),
  });
}

export function useDeleteCustomFieldDefinition(): UseMutationResult<
  null,
  unknown,
  DeleteCustomFieldDefinitionVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) => deleteCustomFieldDefinition(variables.fieldId),
    onSuccess: (_result, variables) => {
      queryClient.setQueryData(
        queryKeys.boards.customFields(variables.boardId),
        (previous) =>
          removeCustomFieldDefinition(
            previous as readonly CustomFieldDefinition[] | undefined,
            variables.fieldId,
          ),
      );
      queryClient.setQueriesData<unknown>(
        { queryKey: queryKeys.cards.all() },
        (previous: unknown) =>
          removeCachedCardCustomFieldDefinition(previous, variables.fieldId),
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.customFields(variables.boardId),
      });
      invalidateCustomFieldPresentation(queryClient, variables.boardId);
    },
    onError: () => announce(t('primitives.customFieldHooks.deleteDefinitionFailed')),
  });
}

export function useCreateCustomFieldOption(): UseMutationResult<
  CustomFieldDefinition,
  unknown,
  CreateCustomFieldOptionVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) =>
      createCustomFieldOption(variables.fieldId, { label: variables.label }),
    onSuccess: (definition, variables) =>
      handleDefinitionSuccess(queryClient, variables.boardId, definition),
    onError: () => announce(t('primitives.customFieldHooks.addOptionFailed')),
  });
}

export function useRenameCustomFieldOption(): UseMutationResult<
  CustomFieldDefinition,
  unknown,
  RenameCustomFieldOptionVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) =>
      renameCustomFieldOption(variables.optionId, { label: variables.label }),
    onSuccess: (definition, variables) =>
      handleDefinitionSuccess(queryClient, variables.boardId, definition),
    onError: () => announce(t('primitives.customFieldHooks.renameOptionFailed')),
  });
}

export function useMoveCustomFieldOption(): UseMutationResult<
  CustomFieldDefinition,
  unknown,
  MoveCustomFieldOptionVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) =>
      moveCustomFieldOption(variables.optionId, {
        previousOptionId: variables.previousOptionId,
        nextOptionId: variables.nextOptionId,
      }),
    onSuccess: (definition, variables) =>
      handleDefinitionSuccess(queryClient, variables.boardId, definition),
    onError: () => announce(t('primitives.customFieldHooks.reorderOptionFailed')),
  });
}

export function useDeleteCustomFieldOption(): UseMutationResult<
  CustomFieldDefinition,
  unknown,
  DeleteCustomFieldOptionVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) => deleteCustomFieldOption(variables.optionId),
    onSuccess: (definition, variables) =>
      handleDefinitionSuccess(queryClient, variables.boardId, definition),
    onError: () => announce(t('primitives.customFieldHooks.deleteOptionFailed')),
  });
}

export function useUpdateCardCustomFieldValue(): UseMutationResult<
  CardDetail,
  unknown,
  UpdateCardCustomFieldValueVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) =>
      patchCardCustomFieldValue(
        variables.cardId,
        variables.fieldId,
        variables.body ?? variables.value,
      ),
    onSuccess: (detail, variables) =>
      handleValueSuccess(queryClient, variables.boardId, detail),
    onError: () => announce(t('primitives.customFieldHooks.updateValueFailed')),
  });
}

export function useClearCardCustomFieldValue(): UseMutationResult<
  CardDetail,
  unknown,
  ClearCardCustomFieldValueVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) =>
      deleteCardCustomFieldValue(variables.cardId, variables.fieldId),
    onSuccess: (detail, variables) =>
      handleValueSuccess(queryClient, variables.boardId, detail),
    onError: () => announce(t('primitives.customFieldHooks.clearValueFailed')),
  });
}
